// Word list validation utilities
// Checks for duplicates, alphabetical patterns, inappropriate words, proper nouns, etc.

import { BLOCKLIST } from '@/data/blocklist';

export interface WordlistValidationReport {
  duplicates: string[];
  alphabeticalPatterns: Array<{ start: number; end: number; words: string[] }>;
  inappropriateWords: string[];
  properNouns: string[];
  trademarkWords: string[];
  externalOverlap: string[];
  invalidFormat: string[];
  totalWords: number;
  validWords: number;
  passed: boolean;
  warnings: string[];
  errors: string[];
}

// Find duplicate words
export function findDuplicates(words: string[]): string[] {
  const seen = new Map<string, number>();
  const duplicates: string[] = [];
  
  words.forEach((word) => {
    const count = seen.get(word) || 0;
    seen.set(word, count + 1);
    if (count === 1) {
      duplicates.push(word);
    }
  });
  
  return duplicates;
}

// Find alphabetical patterns (5+ consecutive alphabetically sorted words)
export function findAlphabeticalPatterns(words: string[]): Array<{ start: number; end: number; words: string[] }> {
  const patterns: Array<{ start: number; end: number; words: string[] }> = [];
  let currentPattern: string[] = [words[0]];
  let startIndex = 0;
  
  for (let i = 1; i < words.length; i++) {
    if (words[i] > words[i - 1]) {
      currentPattern.push(words[i]);
    } else {
      if (currentPattern.length >= 5) {
        patterns.push({
          start: startIndex,
          end: i - 1,
          words: [...currentPattern]
        });
      }
      currentPattern = [words[i]];
      startIndex = i;
    }
  }
  
  // Check final pattern
  if (currentPattern.length >= 5) {
    patterns.push({
      start: startIndex,
      end: words.length - 1,
      words: currentPattern
    });
  }
  
  return patterns;
}

// Find inappropriate words against blocklist
export function findInappropriateWords(words: string[]): string[] {
  const upperWords = words.map(w => w.toUpperCase());
  const upperBlocklist = BLOCKLIST.map(w => w.toUpperCase());
  return upperWords.filter(word => upperBlocklist.includes(word));
}

// Find proper nouns (basic heuristic - words that start with capital in common usage)
export function findProperNouns(words: string[]): string[] {
  // This is a simplified check - in reality, would need a comprehensive proper noun list
  const commonProperNouns = [
    'JESUS', 'ALLAH', 'MOSES', 'PARIS', 'TOKYO', 'CHINA', 'INDIA',
    'JAMES', 'SMITH', 'JONES', 'DAVIS', 'BROWN', 'MARIA', 'DAVID'
  ];
  
  return words.filter(word => commonProperNouns.includes(word.toUpperCase()));
}

// Find trademark/brand words
export function findTrademarkWords(words: string[]): string[] {
  const trademarks = [
    'XEROX', 'APPLE', 'INTEL', 'CISCO', 'ADOBE', 'NOKIA', 'SONY',
    'FEDEX', 'PEPSI', 'ROLEX', 'GUCCI', 'PRADA', 'IKEA', 'LEGOS'
  ];
  
  return words.filter(word => trademarks.includes(word.toUpperCase()));
}

// Validate word format (5 letters, A-Z only)
export function validateWordFormat(words: string[]): { valid: string[]; invalid: string[] } {
  const valid: string[] = [];
  const invalid: string[] = [];
  
  words.forEach(word => {
    if (word.length === 5 && /^[A-Z]+$/i.test(word)) {
      valid.push(word);
    } else {
      invalid.push(word);
    }
  });
  
  return { valid, invalid };
}

// Find overlap with external word lists
export function findExternalOverlap(words: string[]): string[] {
  // Sample of known external puzzle solutions for overlap detection
  const knownExternalWords = [
    'CIGAR', 'REBUT', 'SISSY', 'HUMPH', 'AWAKE', 'BLUSH', 'FOCAL',
    'EVADE', 'NAVAL', 'SERVE', 'HEATH', 'DWARF', 'MODEL', 'KARMA',
    'STINK', 'GRADE', 'QUIET', 'BENCH', 'ABATE', 'FEIGN', 'MAJOR'
  ];
  
  const upperWords = words.map(w => w.toUpperCase());
  return upperWords.filter(word => knownExternalWords.includes(word));
}

// Check vowel distribution for difficulty balance
export function checkVowelDistribution(words: string[]): { balanced: number; veryHard: number; veryEasy: number } {
  const vowels = new Set(['A', 'E', 'I', 'O', 'U']);
  let balanced = 0;
  let veryHard = 0; // 0-1 vowels
  let veryEasy = 0; // 4-5 vowels
  
  words.forEach(word => {
    const vowelCount = word.split('').filter(c => vowels.has(c.toUpperCase())).length;
    
    if (vowelCount <= 1) veryHard++;
    else if (vowelCount >= 4) veryEasy++;
    else balanced++;
  });
  
  return { balanced, veryHard, veryEasy };
}

// Master validation function
export function runFullWordlistValidation(words: string[]): WordlistValidationReport {
  const duplicates = findDuplicates(words);
  const alphabeticalPatterns = findAlphabeticalPatterns(words);
  const inappropriateWords = findInappropriateWords(words);
  const properNouns = findProperNouns(words);
  const trademarkWords = findTrademarkWords(words);
  const externalOverlap = findExternalOverlap(words);
  const { valid: validWords, invalid: invalidFormat } = validateWordFormat(words);
  
  const errors: string[] = [];
  const warnings: string[] = [];
  
  if (duplicates.length > 0) {
    errors.push(`Found ${duplicates.length} duplicate words`);
  }
  
  if (inappropriateWords.length > 0) {
    errors.push(`Found ${inappropriateWords.length} inappropriate/offensive words`);
  }
  
  if (invalidFormat.length > 0) {
    errors.push(`Found ${invalidFormat.length} words with invalid format`);
  }
  
  if (alphabeticalPatterns.length > 0) {
    warnings.push(`Found ${alphabeticalPatterns.length} alphabetical patterns (5+ consecutive sorted words)`);
  }
  
  if (properNouns.length > 0) {
    warnings.push(`Found ${properNouns.length} potential proper nouns`);
  }
  
  if (trademarkWords.length > 0) {
    warnings.push(`Found ${trademarkWords.length} trademark/brand words`);
  }
  
  if (externalOverlap.length > 0) {
    warnings.push(`Found ${externalOverlap.length} words that overlap with external word lists`);
  }
  
  if (words.length < 2000) {
    warnings.push(`Word list is small (${words.length} words) - target is 2000-3500`);
  }
  
  const passed = errors.length === 0;
  
  return {
    duplicates,
    alphabeticalPatterns,
    inappropriateWords,
    properNouns,
    trademarkWords,
    externalOverlap,
    invalidFormat,
    totalWords: words.length,
    validWords: validWords.length,
    passed,
    warnings,
    errors
  };
}
